import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlassCard, KYCBadge } from "@kj/design-system";
import { Sparkles } from "lucide-react";
import { useSession, type Gender, type Nationality } from "../store/session";

type Step = 0 | 1 | 2 | 3;

export default function OnboardingPage() {
  const nav = useNavigate();
  const setUser = useSession((s) => s.setUser);
  const [step, setStep] = useState<Step>(0);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [nationality, setNationality] = useState<Nationality>("KR");
  const [gender, setGender] = useState<Gender>("M");
  const [dob, setDob] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");

  function next() { setStep((s) => (Math.min(3, s + 1) as Step)); }
  function prev() { setStep((s) => (Math.max(0, s - 1) as Step)); }

  function computeAge(dobStr: string): number {
    const d = new Date(dobStr);
    if (isNaN(d.getTime())) return 0;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  }

  function finish() {
    const age = computeAge(dob);
    if (age < 18) {
      setError("만 18세 이상만 가입 가능합니다 / 18歳以上のみ登録可能です.");
      return;
    }
    if (!consent) {
      setError("국외이전·이용약관 동의가 필요합니다.");
      return;
    }
    setUser({
      id: crypto.randomUUID(),
      email,
      displayName,
      nationality,
      gender,
      isAdult: true,
      kycVerified: false,
      createdAt: new Date().toISOString(),
    });
    nav("/home", { replace: true });
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <GlassCard padding="lg" radius="2xl" className="space-y-5">
        <header className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block h-8 w-8 rounded-xl bg-gradient-to-br from-primary-500 via-sakura-500 to-trust-500"
          />
          <div>
            <h1 className="text-lg font-semibold">SkyToLove</h1>
            <p className="text-xs text-fg-muted">
              Founding 1,000 · Speak across the sky, meet in translation.
            </p>
          </div>
        </header>

        <div className="flex gap-2 text-xs text-fg-muted">
          {(["email", "profile", "age", "confirm"] as const).map((label, i) => (
            <span
              key={label}
              className={
                "flex-1 rounded-full h-1.5 " +
                (i <= step ? "bg-primary-500" : "bg-white/10")
              }
            />
          ))}
        </div>

        {step === 0 && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">이메일 · Email</h2>
            <p className="text-sm text-fg-muted">
              매칭 소식과 Founding 멤버 알림을 받을 주소.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-base outline-none focus:border-primary-500"
              aria-label="email"
            />
            <button
              type="button"
              onClick={next}
              disabled={!email.includes("@")}
              className="w-full rounded-full bg-primary-500 py-3 text-white font-semibold disabled:opacity-50"
            >다음 / 次へ</button>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">기본 프로필 · Profile</h2>
            <label className="text-xs text-fg-muted">표시 이름</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="준호 / Miyu"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-base outline-none focus:border-primary-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-fg-muted">국적 / Nationality</label>
                <div className="flex gap-2 mt-1">
                  {(["KR", "JP"] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNationality(n)}
                      className={
                        "flex-1 rounded-xl py-2.5 text-sm border " +
                        (nationality === n
                          ? "bg-primary-500/20 border-primary-500 text-white"
                          : "bg-white/5 border-white/10 text-fg-muted")
                      }
                    >
                      {n === "KR" ? "🇰🇷 한국" : "🇯🇵 日本"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-fg-muted">성별 / Gender</label>
                <div className="flex gap-2 mt-1">
                  {(["M", "F"] as const).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGender(g)}
                      className={
                        "flex-1 rounded-xl py-2.5 text-sm border " +
                        (gender === g
                          ? "bg-primary-500/20 border-primary-500 text-white"
                          : "bg-white/5 border-white/10 text-fg-muted")
                      }
                    >
                      {g === "M" ? "남성" : "여성"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-fg-muted">
              현재 정책상 한국 남성 · 일본 여성만 가입 가능합니다.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={prev} className="flex-1 rounded-full bg-white/5 border border-white/10 py-3">뒤로</button>
              <button
                type="button"
                onClick={next}
                disabled={
                  !displayName.trim() ||
                  !(
                    (nationality === "KR" && gender === "M") ||
                    (nationality === "JP" && gender === "F")
                  )
                }
                className="flex-1 rounded-full bg-primary-500 py-3 text-white font-semibold disabled:opacity-50"
              >다음</button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">연령 확인 · Age</h2>
            <p className="text-sm text-fg-muted">
              만 18세 이상만 가입 가능. KYC는 다음 단계에서.
            </p>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-base outline-none focus:border-primary-500"
              aria-label="date of birth"
            />
            <div className="flex gap-2">
              <button type="button" onClick={prev} className="flex-1 rounded-full bg-white/5 border border-white/10 py-3">뒤로</button>
              <button
                type="button"
                onClick={next}
                disabled={computeAge(dob) < 18}
                className="flex-1 rounded-full bg-primary-500 py-3 text-white font-semibold disabled:opacity-50"
              >다음</button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-3">
            <h2 className="text-xl font-semibold">약관 동의 · Consent</h2>
            <ul className="text-sm text-fg-muted space-y-1 list-inside list-disc">
              <li>개인정보 처리방침 확인</li>
              <li>국외 이전 동의(PIPA · APPI)</li>
              <li>18+ 본인 확인 의무 숙지</li>
            </ul>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="h-4 w-4"
              />
              위 사항에 동의합니다 / 上記に同意します
            </label>
            <div className="bg-white/5 rounded-xl p-3 text-xs text-fg-muted flex items-start gap-2">
              <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-primary-500" />
              <span>
                결제는 오픈 시점까지 <b>일시 중지</b> · 현재 가입자는
                모든 기능 <b>Founding Access</b>로 무료 이용 가능합니다.
              </span>
            </div>
            {error && (
              <div className="bg-danger-500/10 text-danger-500 text-xs rounded-xl p-2 border border-danger-500/30">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={prev} className="flex-1 rounded-full bg-white/5 border border-white/10 py-3">뒤로</button>
              <button
                type="button"
                onClick={finish}
                className="flex-1 rounded-full bg-primary-500 py-3 text-white font-semibold"
              >가입 완료</button>
            </div>
            <p className="text-[0.7rem] text-fg-muted">
              <KYCBadge size="sm" label="Verified pending" className="mr-1" />
              파란 체크는 KYC 제출 후 획득. 오픈 시점에 반영됩니다.
            </p>
          </section>
        )}
      </GlassCard>
    </div>
  );
}
